/**
 * Funnel variant detection.
 *
 * Variants:
 *   - 'organic'          — Default. Proven 24-step funnel with all original copy.
 *                          What converts organic search/referral traffic.
 *   - 'meta-advertorial' — Linda-story advertorial pre-page → full 24-step funnel.
 *                          Designed to warm cold paid Meta traffic before the quiz.
 *   - 'organic-v2'       — Organic A/B treatment (docs/FUNNEL-ORGANIC-V2.md): the
 *                          organic list minus the app's `minimal` cuts, plus the
 *                          app's 4.33 illustrated look. Assigned by a random split
 *                          of NEW organic visitors (VITE_ORGANIC_V2_SPLIT, default
 *                          0 = off) or forced with ?variant=organic-v2.
 *   - 'meta-short'       — 10-step short funnel: DemoScan → UserScan →
 *                          KidneyStatus → Weight → Comorbidities → SocialProof →
 *                          Email → Offer → CreateAccount → Success. Fastest cold
 *                          paid Meta path; DemoScan preview doubles as the ad hook.
 *
 * URL parameter mapping (for ad links):
 *   - PRODUCTION: use opaque short codes via `?ref=...` so users see what looks
 *     like a generic referral/tracking code, not an obvious A/B test query.
 *       start.kidneypal.app/?ref=ks7m9q   → meta-short
 *       start.kidneypal.app/?ref=ka3xnb   → meta-advertorial
 *       (no param)                         → organic
 *   - DEV / INTERNAL: `?variant=meta-short` etc. still works for explicit testing.
 *   - Unknown / missing values fall through to organic (or, while
 *     VITE_ORGANIC_V2_SPLIT > 0, to the organic / organic-v2 split for a
 *     brand-new visitor — see experimentCore.resolveVariantAssignment).
 *
 * Removed 2026-06-06: `'meta'` (full-funnel + soft copy) — was functionally
 * identical to 'organic' after we pulled back metaCopy.ts to a single softened
 * item. Old `?variant=meta` URLs gracefully fall through to 'organic'.
 *
 * Variant is captured once at app load (main.tsx) and persisted in localStorage so
 * navigating deeper into the funnel keeps the right copy even if the user refreshes.
 * The captured value is also pinned in memory for the life of the page, so a
 * storage loss mid-session (iOS WebKit) cannot switch the step list.
 *
 * To override during testing:
 *   - Set variant: start.kidneypal.app/?variant=meta-short
 *   - Reset to organic: start.kidneypal.app/?variant=organic
 *   - Clear: clear localStorage in devtools
 */

import { parseSplitPercent, pinnedVariant, resolveVariantAssignment, type VariantPin } from './experimentCore'
import { ENROLLMENT_KEY, readEnrollment } from './trackingCore'

const VARIANT_KEY = 'kidneypal_funnel_variant'
const EXPIRY_DAYS = 90
/** zustand persist key of the onboarding store (store/onboardingStore.ts). */
const ONBOARDING_STATE_KEY = 'kidneypal-onboarding-v1'

export type FunnelVariant = 'organic' | 'organic-v2' | 'meta-advertorial' | 'meta-short'

const ALL_VARIANTS: FunnelVariant[] = ['organic', 'organic-v2', 'meta-advertorial', 'meta-short']

/** Percent of NEW organic visitors sent to organic-v2 (build-time; default 0 = test off). */
export const ORGANIC_V2_SPLIT = parseSplitPercent(import.meta.env.VITE_ORGANIC_V2_SPLIT)

/**
 * Opaque ad-link codes → variant. These look like generic referral/campaign
 * codes to a casual observer; nothing about them tells the visitor they're in
 * an A/B test or short funnel.
 *
 * Change codes here if they ever leak or you want to rotate. Update the URLs
 * in Meta Ads Manager when you do.
 */
const CODE_TO_VARIANT: Record<string, FunnelVariant> = {
  ks7m9q: 'meta-short',
  ka3xnb: 'meta-advertorial',
  // No code for 'organic' — it's the default (any unmatched/missing param).
}

interface StoredVariant {
  variant: FunnelVariant
  capturedAt: number
  /** How it was assigned ('ref' | 'explicit' | 'returning' | 'split'). Absent on records written before organic-v2. */
  assignedBy?: string
}

/** Set by captureVariant(); getVariant() returns it for the life of the page (see experimentCore.pinnedVariant). */
let pin: VariantPin | null = null

/**
 * Read variant from URL and persist. Tries (in order):
 *   1. `?ref=<code>` — production opaque-code path (looks like a tracking
 *      param to users; the actual variant is hidden behind the code mapping)
 *   2. `?variant=<name>` — dev/internal explicit path (still supported)
 *   3. Existing stored variant from localStorage (or, if that is missing, a
 *      meta-* variant from the enrollment record)
 *   4. 'organic' default — or, while VITE_ORGANIC_V2_SPLIT > 0, a one-time
 *      organic / organic-v2 draw for a brand-new visitor. A browser that
 *      already has funnel state (enrollment record or saved quiz progress) but
 *      no stored variant is a returning organic visitor: it is pinned to
 *      'organic' and never enters the split.
 *
 * Call once on app load (before initEnrollment, so a new visitor's enrollment
 * records the arm it was drawn into).
 */
export function captureVariant(): FunnelVariant {
  if (typeof window === 'undefined') return 'organic'

  const params = new URLSearchParams(window.location.search)
  const code = params.get('ref')
  const explicit = params.get('variant')
  const stored = readStored()

  const result = resolveVariantAssignment({
    refVariant: code && CODE_TO_VARIANT[code] ? CODE_TO_VARIANT[code] : null,
    explicitVariant: explicit && (ALL_VARIANTS as string[]).includes(explicit) ? explicit : null,
    storedVariant: stored?.variant ?? null,
    storedAssignedBy: stored?.assignedBy ?? null,
    enrollmentVariant: enrollmentVariant(),
    isReturning: hasFunnelState(),
    splitPercent: ORGANIC_V2_SPLIT,
    random: Math.random,
  })

  const record = result.persist
    ? persist(result.variant as FunnelVariant, result.source)
    : stored
  pin = { variant: result.variant, record: record ? JSON.stringify(record) : null }
  return result.variant as FunnelVariant
}

function localStore(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    return null
  }
}

/** Variant of an unexpired enrollment record (first landing), else null. */
function enrollmentVariant(): string | null {
  const e = readEnrollment(localStore())
  const age = e ? Date.now() - Date.parse(e.first_seen_at) : NaN
  if (!e || !(age >= 0 && age <= EXPIRY_DAYS * 24 * 60 * 60 * 1000)) return null
  return (ALL_VARIANTS as string[]).includes(e.variant) ? e.variant : null
}

function hasFunnelState(): boolean {
  try {
    return localStorage.getItem(ENROLLMENT_KEY) !== null || localStorage.getItem(ONBOARDING_STATE_KEY) !== null
  } catch {
    return false
  }
}

function persist(variant: FunnelVariant, assignedBy: string): StoredVariant {
  const stored: StoredVariant = { variant, capturedAt: Date.now(), assignedBy }
  try {
    localStorage.setItem(VARIANT_KEY, JSON.stringify(stored))
  } catch {}
  return stored
}

/** Unexpired, valid stored record (expired records are removed). */
function readStored(): StoredVariant | null {
  try {
    const raw = localStorage.getItem(VARIANT_KEY)
    if (!raw) return null

    const stored: StoredVariant = JSON.parse(raw)
    const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000
    if (Date.now() - stored.capturedAt > expiryMs) {
      localStorage.removeItem(VARIANT_KEY)
      return null
    }

    return (ALL_VARIANTS as string[]).includes(stored.variant) ? stored : null
  } catch {
    return null
  }
}

/**
 * Current variant: the one captureVariant() resolved for this page load, held
 * for the life of the page even if storage loses it (iOS WebKit, Oct 2026 —
 * see experimentCore.pinnedVariant). Before capture it reads storage:
 * 'organic' if none stored or expired, and a split-assigned organic-v2 reads
 * as 'organic' while the split is 0 (kill switch — see
 * experimentCore.resolveVariantAssignment).
 */
export function getVariant(): FunnelVariant {
  if (typeof window === 'undefined') return 'organic'
  return pinnedVariant(pin, localStore(), VARIANT_KEY, () => {
    const stored = readStored()
    if (!stored) return 'organic'
    if (stored.variant === 'organic-v2' && stored.assignedBy === 'split' && ORGANIC_V2_SPLIT <= 0) return 'organic'
    return stored.variant
  }) as FunnelVariant
}

/** True for any meta* variant — these all use Meta-compliant copy via metaCopy.ts. */
export function isMetaVariant(): boolean {
  return getVariant().startsWith('meta')
}

/** True only for the short-funnel variant. */
export function isShortVariant(): boolean {
  return getVariant() === 'meta-short'
}

/** True only for the organic A/B treatment arm (organic-v2). */
export function isOrganicV2Variant(): boolean {
  return getVariant() === 'organic-v2'
}

/**
 * App 4.33 look (illustrated icons, header wash, slate type) — every variant
 * since 2026-10-01 (Cam: all versions use the app's icons). It started as the
 * organic-v2 treatment; the organic A/B now differs only in its step list
 * (onboardingStore). The emoji branches behind this go once that test ends.
 */
export function usesAppLook(): boolean {
  return true
}

/** True only for the advertorial-first variant. */
export function isAdvertorialVariant(): boolean {
  return getVariant() === 'meta-advertorial'
}

/**
 * Loops contact `source` for the current variant. Used to segment paid-Meta
 * leads from organic in the Loops audience. Does NOT affect email automation —
 * those key off the `funnel_email_captured` event, not source. Organic keeps the
 * original 'kidneypal_funnel' so the existing baseline cohort is unchanged.
 */
const VARIANT_TO_LOOPS_SOURCE: Record<FunnelVariant, string> = {
  organic: 'kidneypal_funnel',
  // Same source as control on purpose: Loops segments / automations must treat
  // both organic arms identically, or email would confound the read.
  'organic-v2': 'kidneypal_funnel',
  'meta-short': 'meta_short_funnel',
  'meta-advertorial': 'meta_advertorial_funnel',
}

export function getLoopsSource(): string {
  return VARIANT_TO_LOOPS_SOURCE[getVariant()]
}
